/**
 * LocationVerificationService - Verify RBT check-in locations against appointment addresses
 * Uses GPS coordinates and geocoding to ensure RBTs are at assigned locations
 */

import { mileageService } from './MileageService';

// Maximum allowed distance from appointment location (in miles)
const DEFAULT_MAX_DISTANCE_MILES = 0.25; // Quarter mile (~400 meters)

export interface LocationVerificationResult {
  isVerified: boolean;
  distance: number; // miles
  maxAllowedDistance: number; // miles
  requiresApproval: boolean;
  warningMessage?: string;
  checkInLocation: {
    latitude: number;
    longitude: number;
  };
  appointmentLocation: {
    latitude: number;
    longitude: number;
    address?: string;
  };
}

export interface LocationAuditLog {
  id: string;
  sessionNoteId: string;
  appointmentId: string;
  rbtId: string;
  rbtName: string;
  timestamp: Date;
  verificationResult: LocationVerificationResult;
  approvedBy?: string;
  approvedAt?: Date;
  approvalNotes?: string;
  status: 'verified' | 'flagged' | 'approved' | 'rejected';
}

class LocationVerificationService {
  private maxDistanceMiles: number = DEFAULT_MAX_DISTANCE_MILES;

  /**
   * Set the maximum allowed distance for location verification (admin configuration)
   */
  setMaxDistance(miles: number): void {
    if (miles <= 0) {
      throw new Error('Max distance must be greater than 0');
    }
    this.maxDistanceMiles = miles;
  }

  /**
   * Get current max allowed distance
   */
  getMaxDistance(): number {
    return this.maxDistanceMiles;
  }

  /**
   * Verify if RBT check-in location is within acceptable range of appointment location
   */
  verifyLocation(
    checkInLat: number,
    checkInLon: number,
    appointmentLat: number,
    appointmentLon: number,
    appointmentAddress?: string,
    customMaxDistance?: number
  ): LocationVerificationResult {
    // Validate coordinates
    if (!mileageService.isValidCoordinates(checkInLat, checkInLon)) {
      throw new Error('Invalid check-in coordinates');
    }
    if (!mileageService.isValidCoordinates(appointmentLat, appointmentLon)) {
      throw new Error('Invalid appointment coordinates');
    }

    // Calculate distance between check-in and appointment locations
    const distance = mileageService.calculateDistance(
      checkInLat,
      checkInLon,
      appointmentLat,
      appointmentLon
    );

    const maxAllowed = customMaxDistance || this.maxDistanceMiles;
    const isVerified = distance <= maxAllowed;
    const requiresApproval = !isVerified;

    let warningMessage: string | undefined;
    if (!isVerified) {
      warningMessage = `Check-in location is ${distance.toFixed(2)} miles from appointment address. This exceeds the maximum allowed distance of ${maxAllowed} miles and requires supervisor approval.`;
    }

    return {
      isVerified,
      distance,
      maxAllowedDistance: maxAllowed,
      requiresApproval,
      warningMessage,
      checkInLocation: {
        latitude: checkInLat,
        longitude: checkInLon,
      },
      appointmentLocation: {
        latitude: appointmentLat,
        longitude: appointmentLon,
        address: appointmentAddress,
      },
    };
  }

  /**
   * Create a location audit log entry
   */
  createAuditLog(
    sessionNoteId: string,
    appointmentId: string,
    rbtId: string,
    rbtName: string,
    verificationResult: LocationVerificationResult
  ): LocationAuditLog {
    const status: LocationAuditLog['status'] = verificationResult.isVerified
      ? 'verified'
      : 'flagged';

    return {
      id: `audit_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      sessionNoteId,
      appointmentId,
      rbtId,
      rbtName,
      timestamp: new Date(),
      verificationResult,
      status,
    };
  }

  /**
   * Approve a flagged location
   */
  approveLocation(
    auditLog: LocationAuditLog,
    approvedBy: string,
    approvalNotes?: string
  ): LocationAuditLog {
    return {
      ...auditLog,
      status: 'approved',
      approvedBy,
      approvedAt: new Date(),
      approvalNotes,
    };
  }

  /**
   * Reject a flagged location
   */
  rejectLocation(
    auditLog: LocationAuditLog,
    rejectedBy: string,
    rejectionNotes?: string
  ): LocationAuditLog {
    return {
      ...auditLog,
      status: 'rejected',
      approvedBy: rejectedBy,
      approvedAt: new Date(),
      approvalNotes: rejectionNotes,
    };
  }

  /**
   * Format distance for display
   */
  formatDistance(miles: number): string {
    if (miles < 0.1) {
      // Less than 0.1 miles, show in feet
      const feet = Math.round(miles * 5280);
      return `${feet} ft`;
    }
    return `${miles.toFixed(2)} mi`;
  }

  /**
   * Get verification status badge color
   */
  getStatusColor(status: LocationAuditLog['status']): string {
    switch (status) {
      case 'verified':
        return 'var(--color-success-600)';
      case 'approved':
        return 'var(--color-success-500)';
      case 'flagged':
        return 'var(--color-warning-600)';
      case 'rejected':
        return 'var(--color-error-600)';
      default:
        return 'var(--color-gray-600)';
    }
  }

  /**
   * Get verification status display text
   */
  getStatusText(status: LocationAuditLog['status']): string {
    switch (status) {
      case 'verified':
        return 'Verified';
      case 'approved':
        return 'Approved';
      case 'flagged':
        return 'Needs Approval';
      case 'rejected':
        return 'Rejected';
      default:
        return 'Unknown';
    }
  }

  /**
   * Convert address to coordinates (geocoding)
   * Note: This is a placeholder - would need Google Maps API or similar
   */
  async geocodeAddress(address: string): Promise<{
    latitude: number;
    longitude: number;
  } | null> {
    // TODO: Implement actual geocoding with Google Maps Geocoding API
    // For now, return null to indicate geocoding is not yet implemented
    console.warn(
      'Geocoding not implemented. Please manually enter GPS coordinates for appointments.'
    );
    return null;
  }

  /**
   * Reverse geocode coordinates to address
   * Uses the mileageService's reverseGeocode method
   */
  async reverseGeocode(
    latitude: number,
    longitude: number
  ): Promise<string> {
    return mileageService.reverseGeocode(latitude, longitude);
  }

  /**
   * Check if a location audit log requires supervisor review
   */
  requiresSupervisorReview(auditLog: LocationAuditLog): boolean {
    return auditLog.status === 'flagged';
  }

  /**
   * Get summary statistics for location verifications
   */
  getVerificationStats(auditLogs: LocationAuditLog[]): {
    total: number;
    verified: number;
    flagged: number;
    approved: number;
    rejected: number;
    averageDistance: number;
    verificationRate: number; // percentage
  } {
    const total = auditLogs.length;
    const verified = auditLogs.filter((log) => log.status === 'verified').length;
    const flagged = auditLogs.filter((log) => log.status === 'flagged').length;
    const approved = auditLogs.filter((log) => log.status === 'approved').length;
    const rejected = auditLogs.filter((log) => log.status === 'rejected').length;

    const totalDistance = auditLogs.reduce(
      (sum, log) => sum + log.verificationResult.distance,
      0
    );
    const averageDistance = total > 0 ? totalDistance / total : 0;

    const successfulVerifications = verified + approved;
    const verificationRate = total > 0 ? (successfulVerifications / total) * 100 : 100;

    return {
      total,
      verified,
      flagged,
      approved,
      rejected,
      averageDistance,
      verificationRate,
    };
  }
}

export const locationVerificationService = new LocationVerificationService();
